import React, { useRef } from 'react';
import { IClassReportData } from 'models/IOrganizationStats';
import './StudentPerformanceChart.css';

interface ClassComparisonChartProps {
    classes: IClassReportData[];
}

const ClassComparisonChart: React.FC<ClassComparisonChartProps> = ({ classes }) => {
    const tooltipRef = useRef<HTMLDivElement>(null);

    // Filter out classes without data and sort by tasks_solved descending
    const sortedClasses = [...classes]
        .filter(classData => classData.tasks_solved > 0 && !classData.error)
        .sort((a, b) => b.tasks_solved - a.tasks_solved);

    // If no classes have data, show empty state
    if (sortedClasses.length === 0) {
        return (
            <div className="chart-container" style={{ position: 'relative', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center', color: '#6b7280' }}>
                    <p style={{ fontSize: '16px', margin: '0 0 8px 0' }}>No data available</p>
                    <p style={{ fontSize: '14px', margin: '0' }}>Classes with activity data will appear here</p>
                </div>
            </div>
        );
    }

    // Calculate dynamic chart dimensions based on number of classes
    const minChartWidth = 800;
    const maxChartWidth = 4000;
    const optimalBarWidth = 35;
    const minBarWidth = 20;
    
    // Calculate required width for optimal display
    const groupWidth = optimalBarWidth * 2.5; // space for 2 bars + gap
    const minBarSpacing = 15;
    const requiredWidth = sortedClasses.length * (groupWidth + minBarSpacing) + minBarSpacing;
    
    // Calculate additional space needed for class labels (estimate max label width)
    const maxLabelLength = Math.max(...sortedClasses.map(c => c.class_name.length));
    const estimatedLabelWidth = Math.min(120, maxLabelLength * 7); // ~7px per character, max 120px
    const marginsWithLabels = 160 + estimatedLabelWidth; // base margins + label space
    
    // Set chart width with limits - prioritize fitting all content
    const minRequiredWidth = requiredWidth + marginsWithLabels;
    const chartWidth = Math.max(minChartWidth, Math.min(maxChartWidth, minRequiredWidth));
    const chartHeight = 400;
    const leftMargin = 80;
    const rightMargin = Math.max(80, estimatedLabelWidth / 2 + 40); // Ensure right margin accounts for label width
    const topMargin = 50;
    const bottomMargin = 80;
    const plotWidth = chartWidth - leftMargin - rightMargin;
    const plotHeight = chartHeight - topMargin - bottomMargin;

    // Calculate max values for scaling
    const maxTasks = Math.max(...sortedClasses.map(c => c.tasks_solved));
    const maxChallenge = Math.max(...sortedClasses.map(c => c.challenge_score));
    
    // Round up to nice numbers
    const tasksMax = Math.ceil(maxTasks / 500) * 500;
    const challengeMax = Math.ceil(maxChallenge / 5) * 5;

    // Calculate bar dimensions with dynamic width
    const barWidth = Math.max(minBarWidth, Math.min(optimalBarWidth, plotWidth / (sortedClasses.length * 3))); // 3 = 2 bars + gap
    const dynamicGroupWidth = barWidth * 2.5; // space for 2 bars + gap
    const barSpacing = Math.max(minBarSpacing, (plotWidth - (sortedClasses.length * dynamicGroupWidth)) / (sortedClasses.length + 1));

    const formatNumber = (num: number) => {
        if (num >= 1000) {
            return `${(num / 1000).toFixed(0)}K`;
        }
        return num.toString();
    };

    const handleMouseEnter = (event: React.MouseEvent, className: string, value: number, type: string) => {
        if (!tooltipRef.current) return;

        const tooltip = tooltipRef.current;
        const rect = event.currentTarget.getBoundingClientRect();
        const container = event.currentTarget.closest('.chart-container')?.getBoundingClientRect();
        
        if (!container) return;

        const formattedValue = type === 'Tasks Solved' ? 
            value.toLocaleString() : 
            value.toFixed(2);

        tooltip.innerHTML = `
            <div><strong>${className}</strong></div>
            <div>${type}: ${formattedValue}</div>
        `;

        const tooltipX = rect.left - container.left + rect.width / 2;
        const tooltipY = rect.top - container.top - 10;

        tooltip.style.left = `${tooltipX}px`;
        tooltip.style.top = `${tooltipY}px`;
        tooltip.style.transform = 'translateX(-50%) translateY(-100%)';
        tooltip.classList.add('show');
    };

    const handleMouseLeave = () => {
        if (tooltipRef.current) {
            tooltipRef.current.classList.remove('show');
        }
    };

    // Generate class name labels with alternating heights for overlapping prevention
    const generateClassLabels = () => {
        return sortedClasses.map((classData, index) => {
            const x = leftMargin + barSpacing + (index * (dynamicGroupWidth + barSpacing)) + dynamicGroupWidth / 2;
            const words = classData.class_name.split(' ');
            
            // Alternating heights: even indices (0,2,4...) higher, odd indices (1,3,5...) lower
            const isEven = index % 2 === 0;
            const baseYOffset = isEven ? -14 : 14; // ~1 line height (14px) difference
            
            if (words.length <= 2 || classData.class_name.length <= 15) {
                return (
                    <text 
                        key={index}
                        x={x} 
                        y={chartHeight - 15 + baseYOffset} 
                        textAnchor="middle" 
                        className="axis-label"
                    >
                        {classData.class_name}
                    </text>
                );
            } else {
                // Split into multiple lines for long names
                const midPoint = Math.ceil(words.length / 2);
                const firstLine = words.slice(0, midPoint).join(' ');
                const secondLine = words.slice(midPoint).join(' ');
                
                return (
                    <g key={index}>
                        <text 
                            x={x} 
                            y={chartHeight - 25 + baseYOffset} 
                            textAnchor="middle" 
                            className="axis-label"
                        >
                            {firstLine}
                        </text>
                        <text 
                            x={x} 
                            y={chartHeight - 10 + baseYOffset} 
                            textAnchor="middle" 
                            className="axis-label"
                        >
                            {secondLine}
                        </text>
                    </g>
                );
            }
        });
    };


    return (
        <div className="chart-container" style={{ position: 'relative', height: '500px' }}>
            <div className="chart-legend" style={{ display: 'flex', gap: '30px', marginBottom: '20px' }}>
                <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div 
                        className="legend-color" 
                        style={{ 
                            width: '16px', 
                            height: '16px', 
                            backgroundColor: '#6a56cc', 
                            borderRadius: '2px' 
                        }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>Total Tasks Solved</span>
                </div>
                <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div 
                        className="legend-color" 
                        style={{ 
                            width: '16px', 
                            height: '16px', 
                            backgroundColor: '#f26d6d', 
                            borderRadius: '2px' 
                        }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>Challenge Score</span>
                </div>
            </div>

            <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
                <svg 
                    width={chartWidth} 
                    height={chartHeight} 
                    style={{ 
                        minWidth: `${chartWidth}px`, 
                        height: '450px',
                        display: 'block'
                    }}
                >
                {/* Grid lines */}
                <g className="grid">
                    {Array.from({ length: 11 }, (_, i) => {
                        const y = topMargin + (i * plotHeight / 10);
                        return (
                            <line 
                                key={i}
                                x1={leftMargin} 
                                y1={y} 
                                x2={leftMargin + plotWidth} 
                                y2={y} 
                                stroke="#e5e7eb" 
                                strokeWidth="1"
                            />
                        );
                    })}
                </g>

                {/* Y-axis labels - Tasks (left) */}
                <g className="y-axis-left">
                    {Array.from({ length: 11 }, (_, i) => {
                        const value = tasksMax - (i * tasksMax / 10);
                        const y = topMargin + (i * plotHeight / 10) + 5;
                        return (
                            <text 
                                key={i}
                                x={leftMargin - 5} 
                                y={y} 
                                textAnchor="end" 
                                className="axis-label"
                                style={{ fontSize: '12px', fill: '#6b7280' }}
                            >
                                {formatNumber(value)}
                            </text>
                        );
                    })}
                </g>

                {/* Y-axis labels - Challenge Score (right) */}
                <g className="y-axis-right">
                    {Array.from({ length: 11 }, (_, i) => {
                        const value = challengeMax - (i * challengeMax / 10);
                        const y = topMargin + (i * plotHeight / 10) + 5;
                        return (
                            <text 
                                key={i}
                                x={leftMargin + plotWidth + 5} 
                                y={y} 
                                textAnchor="start" 
                                className="axis-label"
                                style={{ fontSize: '12px', fill: '#6b7280' }}
                            >
                                {value.toFixed(0)}
                            </text>
                        );
                    })}
                </g>

                {/* Bars */}
                <g className="bars">
                    {sortedClasses.map((classData, index) => {
                        const x = leftMargin + barSpacing + (index * (dynamicGroupWidth + barSpacing));
                        
                        // Tasks solved bar
                        const tasksHeight = (classData.tasks_solved / tasksMax) * plotHeight;
                        const tasksY = topMargin + plotHeight - tasksHeight;
                        
                        // Challenge score bar
                        const challengeHeight = (classData.challenge_score / challengeMax) * plotHeight;
                        const challengeY = topMargin + plotHeight - challengeHeight;
                        
                        return (
                            <g key={index} className="bar-group">
                                <rect
                                    className="chart-bar bar-tasks"
                                    x={x}
                                    y={tasksY}
                                    width={barWidth}
                                    height={tasksHeight}
                                    fill="#6a56cc"
                                    style={{ cursor: 'pointer', transition: 'fill 0.2s ease' }}
                                    data-class={classData.class_name}
                                    data-value={classData.tasks_solved}
                                    data-type="Tasks Solved"
                                    onMouseEnter={(e) => handleMouseEnter(e, classData.class_name, classData.tasks_solved, "Tasks Solved")}
                                    onMouseLeave={handleMouseLeave}
                                />
                                <rect
                                    className="chart-bar bar-challenge"
                                    x={x + barWidth + 5}
                                    y={challengeY}
                                    width={barWidth}
                                    height={challengeHeight}
                                    fill="#f26d6d"
                                    style={{ cursor: 'pointer', transition: 'fill 0.2s ease' }}
                                    data-class={classData.class_name}
                                    data-value={classData.challenge_score}
                                    data-type="Challenge Score"
                                    onMouseEnter={(e) => handleMouseEnter(e, classData.class_name, classData.challenge_score, "Challenge Score")}
                                    onMouseLeave={handleMouseLeave}
                                />
                            </g>
                        );
                    })}
                </g>

                {/* Class labels */}
                <g className="class-labels">
                    <defs>
                        <clipPath id="labels-clip">
                            <rect 
                                x={leftMargin} 
                                y={chartHeight - bottomMargin} 
                                width={plotWidth} 
                                height={bottomMargin + 20} 
                            />
                        </clipPath>
                    </defs>
                    <g clipPath="url(#labels-clip)">
                        {generateClassLabels()}
                    </g>
                </g>
                </svg>
            </div>

            <div 
                ref={tooltipRef}
                className="chart-tooltip"
            />
        </div>
    );
};

export default ClassComparisonChart;